import { useState } from 'react';
import { IconPlayerPlay } from "@tabler/icons-react";

interface YouTubePlayerProps {
  youtubeUrl: string;
  poster: string;
  className?: string;
  objectFit?: "cover" | "contain";
}

const YouTubePlayer = ({ 
  youtubeUrl, 
  poster, 
  className = "", 
  objectFit = "cover" 
}: YouTubePlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Extract video ID from YouTube URL
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    
    // Handle shorts
    if (url.includes('/shorts/')) {
      return url.split('/shorts/')[1]?.split(/[?#]/)[0] || null;
    }
    
    // Handle watch?v=
    if (url.includes('v=')) {
      return url.split('v=')[1]?.split(/[&?#]/)[0] || null;
    }
    
    // Handle embed/
    if (url.includes('embed/')) {
      return url.split('embed/')[1]?.split(/[?#]/)[0] || null;
    }
    
    // Handle youtu.be/
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1]?.split(/[?#]/)[0] || null;
    }

    return null;
  };

  const videoId = getYouTubeId(youtubeUrl);

  if (isPlaying && videoId) {
    return (
      <div className={`relative w-full h-full overflow-hidden rounded-lg shadow-lg bg-black ${className}`}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    );
  }

  return (
    <div 
      className={`relative w-full h-full cursor-pointer group overflow-hidden rounded-lg shadow-lg bg-black ${className}`}
      onClick={() => setIsPlaying(true)}
    >
      <img 
        src={poster} 
        alt="Video cover" 
        className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
          objectFit === "cover" ? "object-cover" : "object-contain"
        }`}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors duration-300">
        <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-white/90 text-black shadow-xl transform transition-transform duration-300 group-hover:scale-110">
          <IconPlayerPlay className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" />
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;
